import axios, { AxiosInstance } from 'axios'
import { backOff } from 'exponential-backoff'
import {
  SiteListType,
  sleep,
} from '@black-bear-energy/black-bear-energy-common'
import { SiteUploadRecord } from 'common/types'

export type UpsertCounts = {
  newCount: number
  updateCount: number
}

export async function chunkSiteListUpload(
  sites: SiteUploadRecord[],
  siteListType: SiteListType,
  apiEndpoint: '/sites' | '/solar-leaderboard-sites',
  api: AxiosInstance
): Promise<UpsertCounts> {
  const batches = batchSites(sites)
  const batchCount = batches.length
  const siteBatchCounts: UpsertCounts[] = []
  for (let i = 0; i < batchCount; i++) {
    if (i > 0) {
      // Wait one second between batches to avoid overwhelming the server.
      await sleep(1000)
    }

    const uploadBatchWithBackoff = () =>
      uploadSiteBatch(batches[i], siteListType, apiEndpoint, api)
    const siteBatchCount = await backOff(uploadBatchWithBackoff, {
      startingDelay: 1000,
      jitter: 'full',
      numOfAttempts: 3,
      retry: (error) => {
        // don't retry errors that will fail again, like invalid data or duplicate sites
        if (
          axios.isAxiosError(error) &&
          (error.response?.status === 400 || error.response?.status === 409)
        ) {
          return false
        }
        return true
      },
    })
    siteBatchCounts.push(siteBatchCount)
  }
  const finalCounts: UpsertCounts = siteBatchCounts.reduce(
    (acc, currCount) => ({
      newCount: acc.newCount + currCount.newCount,
      updateCount: acc.updateCount + currCount.updateCount,
    }),
    { newCount: 0, updateCount: 0 }
  )
  return finalCounts
}

function batchSites(sites: SiteUploadRecord[]): SiteUploadRecord[][] {
  // Empirically, the server runs out of memory with large batches, so limit batches to 500 sites.
  const batchSize = 500
  const batchCount = Math.ceil(sites.length / batchSize)
  const batches = []
  for (let i = 0; i < batchCount; i++) {
    const startIndex = i * batchSize
    const batch = sites.slice(startIndex, startIndex + batchSize)
    batches.push(batch)
  }
  return batches
}

async function uploadSiteBatch(
  sites: SiteUploadRecord[],
  siteListType: SiteListType,
  apiEndpoint: '/sites' | '/solar-leaderboard-sites',
  api: AxiosInstance
): Promise<UpsertCounts> {
  const response = await api.post<{
    newRecordCount: number
    updatedRecordCount: number
  }>(apiEndpoint, {
    sites,
    siteListType,
  })

  return {
    newCount: response.data.newRecordCount,
    updateCount: response.data.updatedRecordCount,
  }
}
